import { RoleInfo, RoleResponse } from '../services/api';

/** Default agency role assigned via WorkOS; not shown in user-facing role pickers. */
export const HIDDEN_USER_ROLE_SLUGS: readonly string[] = ['member'] as const;

/** Permission slug for users assignable as visit clinicians (mirrors backend). */
export const CLINIC_ASSIGNABLE_CLINICIAN_PERMISSION =
  'clinic:assignable-as-clinician';

/** System role slug for assignable-as-clinician (mirrors backend). */
export const CLINIC_CLINICIAN_ROLE_SLUG = 'clinic:clinician';

/**
 * Clinic roles hidden from the role grid.
 *
 * `clinic:approver` grants `clinic:assignable-as-approver`, which gates nothing:
 * its `is_user_approver` helper has no callers and no screen reads the
 * `is_approver` flag the API returns. Offering it only gives operators a choice
 * with no consequence, in the grid this project exists to simplify.
 *
 * Hidden, not removed. The permission is still in the set that decides who
 * appears in scheduling, so existing assignments must be preserved on save --
 * see `useRoleAssignmentDialog`. Removing the role for real needs a migration
 * and is tracked separately.
 */
export const HIDDEN_CLINIC_ROLE_SLUGS: readonly string[] = [
  'clinic:approver',
] as const;

/**
 * The agency roles an agency that does not run the Roger EMR is offered.
 *
 * Everything else in the catalogue names something only the EMR has: billing,
 * NOA, episodes, patient management, EMR reporting. Offering those to an intake
 * or coding agency describes a product they have not bought, and the operator
 * cannot tell which of the twenty names apply to them.
 *
 * An allowlist rather than a list of EMR roles to hide, because the two fail in
 * opposite directions. A new EMR role missing from a hide-list leaks; a new
 * non-EMR role missing from this one is merely absent until somebody adds it
 * here, which is the failure worth having. Add to this set when a role becomes
 * useful to an agency with no EMR.
 */
export const NON_EMR_AGENCY_ROLE_SLUGS: readonly string[] = [
  'intake',
  'coder',
  'rogerdocs:dashboard',
  'user-manager',
  // Scribe reports (`reports:access`); the EMR reports role stays EMR-only.
  'report-viewer',
] as const;

/** The subset of `roles` an agency without the Roger EMR is offered. */
export function filterNonEmrAgencyRoles<T extends { slug: string }>(
  roles: readonly T[]
): T[] {
  return roles.filter(role => NON_EMR_AGENCY_ROLE_SLUGS.includes(role.slug));
}

export function isUserVisibleRole(role: RoleResponse | RoleInfo): boolean {
  return !HIDDEN_USER_ROLE_SLUGS.includes(role.slug);
}

export function filterUserVisibleRoles<T extends RoleResponse | RoleInfo>(
  roles: T[]
): T[] {
  return roles.filter(isUserVisibleRole);
}

export function isAssignableClinicianRole(role: RoleResponse): boolean {
  if (role.slug === CLINIC_CLINICIAN_ROLE_SLUG) return true;
  return (
    role.permissions?.some(
      permission => permission.slug === CLINIC_ASSIGNABLE_CLINICIAN_PERMISSION
    ) ?? false
  );
}

/** Union of clinic IDs selected on any assignable-clinician role. */
export function collectAssignableClinicIds(
  clinicRoles: RoleResponse[],
  clinicSelection: ReadonlyMap<string, ReadonlySet<string>>
): Set<string> | undefined {
  const assignableRoles = clinicRoles.filter(isAssignableClinicianRole);
  if (assignableRoles.length === 0) return undefined;

  const ids = new Set<string>();
  for (const role of assignableRoles) {
    for (const clinicId of clinicSelection.get(role.id) ?? []) {
      ids.add(clinicId);
    }
  }
  return ids;
}
